// DAS-2111: "Your Alerts" modal for PortfolioAnalysisView
// Evaluates alert definitions on-the-fly against stock tickers.
// Displays CompactAlertCards in a grid with click-to-expand into AlertCard.

import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box, Typography, CircularProgress, Grid, TextField, InputAdornment,
  Select, MenuItem, FormControl, InputLabel, Pagination, Chip, Stack,
  Dialog, DialogTitle, DialogContent, Button,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CompactAlertCard from '../CompactAlertCard';
import AlertCard from '../AlertCard';
import { fetchAlerts } from '../../helpers/portfolioAnalysisHelper';
import getUserId from '../../functions/getUserId';

const PAModal_Alerts = ({ tickers, data, source = 'portfolio', reduxAlerts = [] }) => {
  const useRedux = source === 'portfolio' && reduxAlerts.length > 0;
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [summary, setSummary] = useState({});
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [page, setPage] = useState(1);
  const [expandedAlert, setExpandedAlert] = useState(null);
  const pageSize = 12;
  const fetchedRef = useRef(false);

  const companyMap = useMemo(() => {
    const map = {};
    if (data && Array.isArray(data)) {
      data.forEach(d => {
        const t = (d.Ticker || '').toUpperCase();
        if (t) map[t] = d.Company || '';
      });
    }
    return map;
  }, [data]);

  useEffect(() => {
    if (useRedux) {
      const enriched = reduxAlerts.map(a => ({
        ...a,
        company: a.company || companyMap[(a.ticker || '').toUpperCase()] || '',
      }));
      setAlerts(enriched);
      setSummary({ tickers_evaluated: tickers?.length || 0, total: enriched.length });
      return;
    }

    if (fetchedRef.current || !tickers || tickers.length === 0) return;
    fetchedRef.current = true;

    setLoading(true);
    const userId = getUserId();
    fetchAlerts(tickers, userId)
      .then(result => {
        const enriched = (result.alerts || []).map(a => ({
          ...a,
          company: a.company || companyMap[(a.ticker || '').toUpperCase()] || '',
        }));
        setAlerts(enriched);
        setSessionId(result.session_id);
        setSummary(result.summary || {});
      })
      .catch(err => console.error('[PAModal_Alerts] Failed to evaluate alerts:', err))
      .finally(() => setLoading(false));
  }, [tickers, companyMap, useRedux, reduxAlerts]);

  const alertTypes = useMemo(() => {
    const types = [...new Set(alerts.map(a => a.alert_type).filter(Boolean))];
    return types.sort();
  }, [alerts]);

  const filtered = useMemo(() => {
    let result = alerts.filter(a => !a.bignore);

    if (filterType !== 'all') {
      result = result.filter(a => a.alert_type === filterType);
    }

    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(a =>
        (a.ticker || '').toLowerCase().includes(q) ||
        (a.alert_subtype || '').toLowerCase().includes(q) ||
        (a.pf_alert_text || '').toLowerCase().includes(q)
      );
    }

    result.sort((a, b) => (b.pf_final_confidence_score || 0) - (a.pf_final_confidence_score || 0));
    return result;
  }, [alerts, filterType, search]);

  const pageCount = Math.ceil(filtered.length / pageSize);
  const paged = filtered.slice((page - 1) * pageSize, page * pageSize);

  const handleCheckboxChange = (id) => {
    setAlerts(prev => prev.map(a => a.ID === id ? { ...a, seen: a.seen ? 0 : 1 } : a));
  };

  const handleFollowedUpChange = (id) => {
    setAlerts(prev => prev.map(a => a.ID === id ? { ...a, followed_up: a.followed_up ? 0 : 1 } : a));
  };

  if (loading) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" sx={{ minHeight: 300 }}>
        <CircularProgress />
        <Typography sx={{ mt: 2, color: 'text.secondary' }}>
          Evaluating alert definitions against {tickers.length} tickers...
        </Typography>
        <Typography variant="caption" sx={{ mt: 1, color: 'text.secondary' }}>
          This may take a moment for large portfolios.
        </Typography>
      </Box>
    );
  }

  if (alerts.length === 0) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" sx={{ minHeight: 200 }}>
        <Typography color="text.secondary">
          No alerts triggered for the {tickers.length} tickers in this portfolio.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Summary bar */}
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2, flexWrap: 'wrap' }}>
        <Chip
          label={`${filtered.length} alert${filtered.length !== 1 ? 's' : ''}`}
          color="primary"
          size="small"
        />
        {alertTypes.map(t => {
          const count = alerts.filter(a => a.alert_type === t && !a.bignore).length;
          return (
            <Chip
              key={t}
              label={`${t}: ${count}`}
              size="small"
              variant={filterType === t ? 'filled' : 'outlined'}
              color={t.toLowerCase() === 'harvest' ? 'error' : 'success'}
              onClick={() => setFilterType(filterType === t ? 'all' : t)}
              sx={{ cursor: 'pointer' }}
            />
          );
        })}
        <Typography variant="caption" sx={{ color: 'text.secondary', ml: 'auto' }}>
          {summary.tickers_evaluated || tickers.length} tickers evaluated
        </Typography>
      </Stack>

      {/* Search + filter controls */}
      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <TextField
          size="small"
          placeholder="Search ticker, subtype, text..."
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
            ),
          }}
          sx={{ flex: 1, maxWidth: 350 }}
        />
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Alert Type</InputLabel>
          <Select
            value={filterType}
            label="Alert Type"
            onChange={e => { setFilterType(e.target.value); setPage(1); }}
          >
            <MenuItem value="all">All types</MenuItem>
            {alertTypes.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
          </Select>
        </FormControl>
      </Stack>

      {/* Alert cards grid */}
      <Grid container spacing={2}>
        {paged.map((alert, idx) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={alert.ID || `pa-${idx}`}>
            <Box
              sx={{
                cursor: 'pointer',
                '& .MuiCard-root': { width: '100%' },
              }}
              onClick={() => setExpandedAlert(alert)}
            >
              <CompactAlertCard
                alert={alert}
                onCheckboxChange={() => handleCheckboxChange(alert.ID)}
                onFollowedUpChange={() => handleFollowedUpChange(alert.ID)}
                onReinvestmentCheck={() => {}}
                openFPDialog={() => {}}
              />
            </Box>
          </Grid>
        ))}
      </Grid>

      {/* Pagination */}
      {pageCount > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={pageCount}
            page={page}
            onChange={(e, p) => setPage(p)}
            color="primary"
            showFirstButton
            showLastButton
          />
        </Box>
      )}

      {/* Expanded alert dialog */}
      {expandedAlert && (
        <Dialog
          open={true}
          onClose={() => setExpandedAlert(null)}
          maxWidth={false}
          fullWidth={false}
          PaperProps={{
            style: {
              maxHeight: '90vh',
              overflow: 'auto',
              width: 'auto',
              maxWidth: '90vw',
              zIndex: 1300,
            },
          }}
        >
          <DialogTitle>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <span>Alert Details</span>
              <Button onClick={() => setExpandedAlert(null)} sx={{ minWidth: 'auto' }}>✕</Button>
            </Box>
          </DialogTitle>
          <DialogContent>
            <AlertCard
              alert={expandedAlert}
              onClose={() => setExpandedAlert(null)}
              onCheckboxChange={() => handleCheckboxChange(expandedAlert.ID)}
              onFollowedUpChange={() => handleFollowedUpChange(expandedAlert.ID)}
              onReinvestmentCheck={() => {}}
            />
          </DialogContent>
        </Dialog>
      )}
    </Box>
  );
};

export default PAModal_Alerts;
